import { useState, useCallback, useEffect } from "react";
import { exportConversations, ExportRequest } from "../services/api/conversationApiService";
import { getUserEmail } from "../../../auth/okta";
import { isConversationExportEnabled as checkPermission } from "../services/config";

/**
 * Where an exporter gets its behavior: who may export, how the CSV is
 * fetched, and what the download is named. Bonds passes nothing (the
 * defaults below are its original hardcoded behavior: the
 * VITE_CONVERSATION_EXPORT_ALLOWED_USERS build list against the signed-in
 * email, daphe's /conversations/export/csv). Athena passes its own driver:
 * enablement resolved server-side by the Athena proxy's /api/features, and
 * the export against the proxy's own endpoint (ADR 0006 in demo-sage-athena).
 *
 * Driver functions must be stable references (module-level consts) — the
 * permission check effect depends on them.
 */
export interface ConversationExporterDriver {
  resolveEnabled?: () => Promise<boolean>;
  fetchCsv?: (params: ExportRequest) => Promise<string>;
  csvFileName?: string;
}

const defaultResolveEnabled = async (): Promise<boolean> => {
  const email = await getUserEmail();
  return checkPermission(email);
};

export const useConversationExporter = (driver: ConversationExporterDriver = {}) => {
  const { resolveEnabled = defaultResolveEnabled, fetchCsv = exportConversations, csvFileName = "conversations.csv" } = driver;

  const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [userEmail, setUserEmail] = useState<string | undefined>(undefined);
  const [isEnabled, setIsEnabled] = useState<boolean>(false);

  const checkPermissions = useCallback(async () => {
    try {
      const email = await getUserEmail();
      setUserEmail(email);
      setIsEnabled(await resolveEnabled());
    } catch (error) {
      console.error("Failed to check permissions:", error);
      setIsEnabled(false);
    }
  }, [resolveEnabled]);

  const handleExport = useCallback(
    async (params: ExportRequest): Promise<void> => {
      setIsLoading(true);
      try {
        const data = await fetchCsv(params);
        if (data.length < 1) {
          throw new Error("No conversations found for the selected time period.");
        }
        downloadAsCSV(data, csvFileName);
        setIsPopupOpen(false);
      } catch (error) {
        console.error("Failed to export conversations:", error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [fetchCsv, csvFileName]
  );

  useEffect(() => {
    checkPermissions();
  }, [checkPermissions]);

  const openPopup = useCallback(() => setIsPopupOpen(true), []);
  const closePopup = useCallback(() => setIsPopupOpen(false), []);

  return {
    isEnabled,
    isPopupOpen,
    isLoading,
    userEmail,
    openPopup,
    closePopup,
    handleExport,
    checkPermissions
  };
};

const downloadAsCSV = (csvContent: string, fileName: string): void => {
  if (!csvContent.trim()) return;

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", fileName);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
